import { Card } from '@payaca/ambrosia';
import { FC, useMemo, type JSX } from 'react';

import BasicField from '@payaca/components/basicField/BasicField';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import ValidatedFieldWrapper from '@payaca/components/validatedFieldWrapper/ValidatedFieldWrapper';

import { useBusinessAccountForm } from './useBusinessAccountForm';
import { useCompanyInfoFormValidators } from '@/hooks/useCompanyInfoValidators';
import SettingsSaveButton from './SettingsSaveButton';
import { useAccount } from '@/api/rest/users/useAccount';
import { useRegion } from '@/api/rest/users/useRegion';
import { cansupportTwilioSenderHandle } from '@payaca/helpers/internationalHelper';

type Props = {
  readOnly: boolean;
};

type FormState = {
  smsSenderHandle: string;
};

const CompanySettingsMessaging: FC<Props> = ({
  readOnly,
}: Props): JSX.Element | null => {
  const account = useAccount();
  const region = useRegion();

  const showTwilioSenderHandle = useMemo(
    () => cansupportTwilioSenderHandle(region),
    [region]
  );

  const initialState: FormState = {
    smsSenderHandle: account.smsSenderHandle ?? '',
  };

  const fieldValidators = useCompanyInfoFormValidators([]);

  const {
    formState,
    updateFormFields,
    formValidationResult,
    saveAndFlush,
    isValid,
    isDirty,
    isSaving,
  } = useBusinessAccountForm<FormState>(initialState, fieldValidators);

  if (!showTwilioSenderHandle) return null;

  return (
    <Card sizeVariant="lg">
      <Card.Body className="flex flex-col gap-2">
        <ValidatedFieldWrapper
          validationResult={formValidationResult.smsSenderHandle}
        >
          <BasicField
            name="smsSenderHandle"
            label={
              'Send SMS messages from (Alphanumeric, 4-11 characters, must contain 1 letter)'
            }
            value={formState.smsSenderHandle}
            isRequired={true}
            styleVariant={InputStyleVariant.OUTSIZE}
            onChange={updateFormFields}
            isDisabled={readOnly}
          />
        </ValidatedFieldWrapper>
        {!readOnly && (
          <SettingsSaveButton
            isDirty={isDirty}
            isSaving={isSaving}
            isValid={isValid}
            onSave={saveAndFlush}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default CompanySettingsMessaging;
